<template>
    <HeadlessRadioGroup
        class="flex flex-wrap gap-4 font-normal"
        :modelValue="value"
        @update:modelValue="v => value = v"
        :by="compareDates"
    >
        <HeadlessRadioGroupOption
            v-slot="{ checked }"
            as="div"
            :key="'soon'"
            :value="soon"
            v-if="show30minutes"
        >
                <button
                    type="button"
                    class="select-none rounded-xl border-2 border-transparent bg-white px-4 py-2 shadow-main outline-none transition-colors hover:border-orange-100 focus-visible:border-orange-100"
                    :class="{
          '!border-orange-200': checked,
        }"
                >
                    Побыстрее
                    <!--Через <span class="text-[#999700]">~ 30 мин</span>-->
                </button>
        </HeadlessRadioGroupOption>
        <HeadlessRadioGroupOption
            v-for="(step, i) in firstPart"
            :key="step.displayValue"
            v-slot="{ checked }"
            as="div"
            :value="step.value"
        >
            <button
                type="button"
                class="select-none rounded-xl border-2 border-transparent bg-white px-4 py-2 shadow-main outline-none transition-colors hover:border-orange-100 focus-visible:border-orange-100"
                :class="{
          '!border-orange-200': checked,
        }"
            >
                {{ step.displayValue }}
            </button>
        </HeadlessRadioGroupOption>
        <button
            v-if="secondPart.length"
            type="button"
            class="grow rounded-xl border-2 border-transparent bg-white px-4 py-2 shadow-main outline-none transition-colors hover:border-orange-100 focus:border-orange-100"
            :class="{
        '!border-orange-200': isChoosedTimeFromSecondPart,
      }"
            @click="showModal = true"
        >
            Выбрать другое время
        </button>

        <div class="w-full flex items-center">
            <div class="grow rounded-xl flex-shrink-0 outline-none transition-colors border-2 border-transparent hover:border-orange-100 focus:border-orange-100">
                <InputDatepicker
                    name="time_deliver"
                    label="Нажмите, чтобы выбрать дату"
                    :with-time="true"
                    :min-date="workingHours.minDate"
                    :max-date="maxDate"
                    :min-time="{hours: workingHours.from_new.hour, minutes: workingHours.from_new.minute}"
                    :max-time="{hours: workingHours.to_new.hour, minutes: workingHours.to_new.minute}"
                    :start-time="workingHours.startTime"
                />
            </div>
            <div class="pl-2 opacity-50 text-sm" v-if="currentReciptionWay.type !== 'restaurant'">
                Привезём<div>с&nbsp;{{intervalFrom}}&nbsp;до&nbsp;{{intervalTo}}</div>
            </div>
        </div>

        <HeadlessTransitionRoot
            appear
            :show="showModal"
            as="template"
        >
            <HeadlessDialog
                as="div"
                class="relative z-50"
                @close="showModal = false"
            >
                <HeadlessTransitionChild
                    as="template"
                    enter="duration-300 ease-out"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="duration-200 ease-in"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <div class="fixed inset-0 bg-black bg-opacity-25">
                        <MouseFollower :hide-when-over-el="dialogPanelEl" />
                    </div>
                </HeadlessTransitionChild>

                <div class="fixed inset-0 overflow-y-auto">
                    <div class="flex min-h-full items-center justify-center p-4 text-center">
                        <HeadlessTransitionChild
                            as="template"
                            enter="duration-300 ease-out"
                            enter-from="opacity-0 scale-95"
                            enter-to="opacity-100 scale-100"
                            leave="duration-200 ease-in"
                            leave-from="opacity-100 scale-100"
                            leave-to="opacity-0 scale-95"
                        >
                            <HeadlessDialogPanel
                                class="w-fit max-w-full transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all"
                            >
                                <div
                                    ref="dialogPanelEl"
                                    class="h-full w-full p-6 pt-12"
                                >
                                    <button class="outline-none absolute left-auto right-2 top-2 text-center text-sm text-black z-10" @click="showModal = false" type="button">
                                        <img src="~/assets/icon/icon-close-gray-black.svg" alt="" class="h-8 rounded-full bg-black opacity-20">
                                    </button>

                                    <div class="grid grid-cols-3 gap-4">
                                        <HeadlessRadioGroupOption
                                            v-for="(step, i) in secondPart"
                                            :key="step.displayValue"
                                            v-slot="{ checked }"
                                            as="template"
                                            :value="step.value"
                                        >
                                            <button
                                                type="button"
                                                class="select-none rounded-xl border-2 border-transparent bg-white px-4 py-2 shadow-main outline-none transition-colors hover:border-orange-100 focus-visible:border-orange-100"
                                                @click="showModal = false"
                                                :class="{
                          '!border-orange-200': checked,
                        }"
                                            >
                                                {{ step.displayValue }}
                                            </button>
                                        </HeadlessRadioGroupOption>
                                    </div>
                                </div>
                            </HeadlessDialogPanel>
                        </HeadlessTransitionChild>
                    </div>
                </div>
            </HeadlessDialog>
        </HeadlessTransitionRoot>
    </HeadlessRadioGroup>
</template>

<script setup lang="ts">
import { DateTime, Duration, Settings } from 'luxon'
import { parseFromHour, parseTime } from '~/composables/api/restaurants'
import { getFirstNItems } from '~/utils'
import { useUser } from '~/composables/api/user'
import { computed } from 'vue'

const emit = defineEmits(['dateChanged'])

const dialogPanelEl = ref()

const { data: currentReciptionWay } = useCurrentReceptionWay()

Settings.defaultZone = 'Europe/Moscow'

let maxDate = new Date()
maxDate.setDate(maxDate.getDate() + 2 * 7)

function compareDates(a: Date, b: Date) {
    return a.getTime() === b.getTime()
}

const { value } = useField<Date | undefined>('time_deliver')

const intervalFrom = ref()
const intervalTo = ref()

watch(value, (newDate) => {
    emit('dateChanged', newDate)

    if (currentReciptionWay && currentReciptionWay.value?.type !== 'restaurant' && currentReciptionWay.value?.delivery_zone?.interval) {
        if(newDate && currentReciptionWay.value?.delivery_zone?.interval > 0) {
            let from = new Date(newDate.getTime() - currentReciptionWay.value?.delivery_zone?.interval*60000);
            intervalFrom.value = from.toTimeString().substr(0,5)

            let to = new Date(newDate.getTime() + currentReciptionWay.value?.delivery_zone?.interval*60000);
            intervalTo.value = to.toTimeString().substr(0,5)
        }
    }

})

const deliveryDuration = ref(40)
let now = DateTime.now()
const soon = ref(now.plus({ minutes: 40 - (now.minute % 10) }).toJSDate())
value.value = soon.value
const {data: user} = useUser((v) => v)
watch(user, async (newUser) => {
     deliveryDuration.value = (newUser?.delivery_duration || 40)
    let deliveryTime = DateTime.now().plus({ minutes: (newUser?.delivery_duration || 40)})
    let roundedTime = deliveryTime.plus({ minutes: 10-(deliveryTime.minute % 10) })
    if (roundedTime.day != deliveryTime.day) {
        roundedTime = deliveryTime
    }
    value.value = roundedTime.toJSDate()
    soon.value = roundedTime.toJSDate()
},
{ immediate: true })

const workingHours = computed(() => {
    let from: DateTime,
        until: DateTime,
        from_new: DateTime,
        to_new: DateTime


    if (currentReciptionWay.value?.type === 'restaurant') {
        const workingHours = parseFromHour(currentReciptionWay.value.from_hour)
        from = DateTime.fromObject({
            ...DateTime.now().toObject(),
            hour: workingHours.fromHours,
            minute: workingHours.fromMinutes,
            second: 0,
            millisecond: 0,
        })
        until = DateTime.fromObject({
            ...DateTime.now().toObject(),
            hour: workingHours.toHours,
            minute: workingHours.toMinutes,
            second: 0,
            millisecond: 0,
        })
    } else {
        let from_hours = 11, from_minutes = 0, to_hours = 22, to_minutes = 30
        if (currentReciptionWay.value?.delivery_zone?.open_from) {
            let workingHours = parseTime(currentReciptionWay.value?.delivery_zone?.open_from)
            from_hours = workingHours.hours
            from_minutes = workingHours.minutes
            workingHours = parseTime(currentReciptionWay.value?.delivery_zone?.closed_from)
            to_hours = workingHours.hours
            to_minutes = workingHours.minutes
        }

        from = DateTime.fromObject({
            ...DateTime.now().toObject(),
            hour: from_hours,
            minute: from_minutes,
            second: 0,
            millisecond: 0,
        })
        until = DateTime.fromObject({
            ...DateTime.now().toObject(),
            hour: to_hours,
            minute: to_minutes,
            second: 0,
            millisecond: 0,
        })

        if(currentReciptionWay.value?.delivery_zone?.only_nearest_time) {
            until = DateTime.now().plus({ minutes: deliveryDuration.value+10 })
            maxDate.setDate((new Date()).getDate())
        }
    }

    from_new = from.plus({ minutes: deliveryDuration.value })
    to_new = until

    let now = DateTime.now();
    let startTime
    let minDate = structuredClone(soon.value)
    if(now > to_new.minus({ minutes: deliveryDuration.value })) {
        minDate = DateTime.now().plus({ day: 1 }).toJSDate()
        minDate.setHours(from_new.hour)
        minDate.setMinutes(from_new.minute)
        startTime = {hours: from_new.hour, minutes: from_new.minute}
        value.value = minDate
    } else if (now < from_new) {
        minDate.setHours(from_new.hour)
        minDate.setMinutes(from_new.minute)
        value.value = structuredClone(soon.value)
        startTime = {hours: from_new.hour, minutes: from_new.minute}
    }

    return { from, until, from_new, to_new, startTime, minDate }
})

const timeSteps = computed(() => {
    const step = Duration.fromObject({ minutes: 30 })

    const steps: {
        value: Date
        displayValue: string
    }[] = []

    if (workingHours.value) {
        for (let t = workingHours.value.from_new; t <= workingHours.value.to_new; t = t.plus(step)) {
            if (DateTime.fromJSDate(soon.value) <= t)
                steps.push({
                    value: t.toJSDate(),
                    displayValue: t.toFormat('HH:mm'),
                })
        }

        return steps
    }

    return []
})

const show30minutes = computed(() => {
    if (workingHours.value) {
        return DateTime.now() >= workingHours.value.from && DateTime.now().plus({ minutes: deliveryDuration.value }) <= workingHours.value.until
    }
    return false
})

const firstPart = computed(() => {
    const steps = getFirstNItems(timeSteps.value, 3)
    return steps
})
const secondPart = computed(() => timeSteps.value.slice(3))

const isChoosedTimeFromSecondPart = computed(() => {
    if (value.value) {
        return !!secondPart.value.find((t) => t.value.getTime() === value.value?.getTime())
    }
    return false
})

const showModal = ref(false)
</script>
